/**
 * Modal de input simple basado en DOM (no en Phaser). Se usa para crear / editar
 * el nombre de un marker del mapa. Reemplaza a window.prompt (UX feo y bloqueante).
 *
 * Crea un overlay full-screen + un panel centrado con un <input> + botones.
 * Maneja Enter (confirmar), Escape (cancelar), click fuera del modal (cancelar).
 * Hace stopPropagation en todos los keydown para que Phaser no capture las teclas.
 */

export interface PromptModalOpts {
  title: string;
  initialValue?: string;
  placeholder?: string;
  maxLength?: number;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (value: string) => void;
  onCancel?: () => void;
}

export function showPromptModal(opts: PromptModalOpts): void {
  if (typeof document === 'undefined') return;

  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed; inset: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex; align-items: center; justify-content: center;
    z-index: 9999;
    font-family: sans-serif;
  `;

  const modal = document.createElement('div');
  modal.style.cssText = `
    background: #111111;
    border: 1px solid #555555;
    border-radius: 4px;
    padding: 18px 18px 14px 18px;
    min-width: 320px;
    max-width: 80vw;
    color: #ffffff;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.6);
    box-sizing: border-box;
  `;
  modal.addEventListener('click', (e) => e.stopPropagation());

  const title = document.createElement('div');
  title.textContent = opts.title;
  title.style.cssText = `
    font-size: 14px;
    font-weight: bold;
    color: #ffd400;
    margin-bottom: 12px;
  `;
  modal.appendChild(title);

  const input = document.createElement('input');
  input.type = 'text';
  input.value = opts.initialValue ?? '';
  if (opts.placeholder) input.placeholder = opts.placeholder;
  if (typeof opts.maxLength === 'number') input.maxLength = opts.maxLength;
  input.style.cssText = `
    width: 100%;
    padding: 8px 10px;
    font-size: 13px;
    color: #ffffff;
    background: #1c1c1c;
    border: 1px solid #444444;
    border-radius: 3px;
    outline: none;
    box-sizing: border-box;
    margin-bottom: 14px;
  `;
  input.addEventListener('focus', () => {
    input.style.borderColor = '#888888';
  });
  input.addEventListener('blur', () => {
    input.style.borderColor = '#444444';
  });
  modal.appendChild(input);

  const buttonRow = document.createElement('div');
  buttonRow.style.cssText = `
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  `;

  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = opts.cancelLabel ?? 'Cancelar';
  cancelBtn.style.cssText = `
    padding: 6px 14px;
    font-size: 12px;
    color: #cccccc;
    background: #2a2a2a;
    border: 1px solid #444444;
    border-radius: 3px;
    cursor: pointer;
  `;
  cancelBtn.addEventListener('mouseover', () => { cancelBtn.style.background = '#3a3a3a'; });
  cancelBtn.addEventListener('mouseout', () => { cancelBtn.style.background = '#2a2a2a'; });

  const confirmBtn = document.createElement('button');
  confirmBtn.textContent = opts.confirmLabel ?? 'Aceptar';
  confirmBtn.style.cssText = `
    padding: 6px 14px;
    font-size: 12px;
    color: #111111;
    background: #ffd400;
    border: 1px solid #ddb800;
    border-radius: 3px;
    cursor: pointer;
    font-weight: bold;
  `;
  confirmBtn.addEventListener('mouseover', () => { confirmBtn.style.background = '#ffea66'; });
  confirmBtn.addEventListener('mouseout', () => { confirmBtn.style.background = '#ffd400'; });

  buttonRow.appendChild(cancelBtn);
  buttonRow.appendChild(confirmBtn);
  modal.appendChild(buttonRow);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    overlay.remove();
  };
  const confirm = (): void => {
    const value = input.value;
    close();
    opts.onConfirm(value);
  };
  const cancel = (): void => {
    close();
    opts.onCancel?.();
  };

  // Click fuera del modal → cancel
  overlay.addEventListener('click', cancel);

  // Capturar TODOS los keydown del input para que Phaser no procese
  // teclas como Tab, M, ESC, etc., mientras estamos tipeando.
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      confirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  });

  cancelBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    cancel();
  });
  confirmBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    confirm();
  });

  // Focus + selección del texto inicial para edit-flows.
  requestAnimationFrame(() => {
    input.focus();
    input.select();
  });
}
